<template>
    <window title="" class="add-win"
        @close="on_close" :tv='tv'>
        <!-- Custom Header -->
        <div class="aw-header">
            <span class="aw-title">Indicators</span>
            <button class="aw-close" @click="on_close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
            </button>
        </div>

        <!-- Full-height Indicator Selector with integrated sidebar -->
        <div class="aw-body">
            <IndicatorSelector
                :indicators="allOvs"
                v-model="selectedIndicators"
                @selection-change="onSelectionChange"
                @apply-to-chart="applySelectedToChart"
                @add-to-group="addToGroup"
                @apply-group="applyGroup"
            />
        </div>
    </window>
</template>
<script>
import Window from '../../components/Window.vue'
import IndicatorSelector from '../../components/IndicatorSelector.vue'
import notifications from '../../utils/notifications.js'
import indicatorGroupsService from '../../services/indicatorGroups.js'

export default {
    name: 'AddWin',
    props: ['id', 'main', 'dc', 'tv', 'data'],
    components: { 
        Window,
        IndicatorSelector
    },
    data() {
        return {
            selectedIndicators: [],
            allOvs: this.tv.overlays.slice()
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        }
    },
    computed: {
        isDarkTheme() {
            return this.$store?.getters?.isDarkTheme || false;
        }
    },
    methods: {
        on_close() { 
            this.$props.main.remove_widget(this.$props.id) 
        },
        
        onSelectionChange(indicators) {
            this.selectedIndicators = indicators
        },
        
        async applyGroup(group) {
            if (group && group.id) {
                try {
                    group = await indicatorGroupsService.setDefaultIndicatorGroup(group.id)
                } catch (error) {
                    console.warn('Failed to persist default group:', error)
                }
            }

            // Register group name in Legend's custom_groups localStorage
            let customGroups = JSON.parse(
                localStorage.getItem('tc_indicator_groups') || '[]'
            )
            if (!customGroups.includes(group.name)) {
                customGroups.push(group.name)
                localStorage.setItem('tc_indicator_groups',
                    JSON.stringify(customGroups))
            }

            // Remove existing overlays belonging to this group to avoid duplicates
            const dc = this.dc
            if (dc && dc.data) {
                const sides = ['onchart', 'offchart']
                // Collect IDs of overlays in this group
                let idsToRemove = []
                sides.forEach(side => {
                    if (!dc.data[side]) return
                    dc.data[side].forEach(ov => {
                        const ovGroup = (ov.settings && ov.settings.group) || ov.group
                        if (ovGroup === group.name) {
                            idsToRemove.push(ov.id)
                        }
                    })
                })
                // Remove each by ID using datacube's del method
                idsToRemove.forEach(id => {
                    try { dc.del(id) } catch (e) { /* ignore */ }
                })
            }

            // Add the updated indicators (only visible ones)
            group.indicators.forEach(ind => {
                if (ind.hidden) return // Skip hidden indicators
                this.main.add_overlay({
                    side: ind.side || this.data.type || 'onchart', 
                    index: this.data.index,
                    type: ind.type, 
                    keepSide: !!ind.side,
                    settings: Object.assign({}, ind.settings || {}, {
                        group: group.name,
                        display: true
                    })
                })
            })
            const visibleCount = group.indicators.filter(i => !i.hidden).length
            notifications.success(`Applied "${group.name}" (${visibleCount} indicators)`)
            this.on_close()
        },
        
        applySelectedToChart() {
            if (!this.selectedIndicators.length) return
            this.selectedIndicators.forEach(ind => {
                this.main.add_overlay({
                    side: ind.side || this.data.type || 'onchart', 
                    index: this.data.index,
                    type: ind.type, 
                    settings: ind.settings || {}
                })
            })
            notifications.success(`Applied ${this.selectedIndicators.length} indicators`)
            this.on_close()
        },

        addToGroup() {
            // The IndicatorSelector handles switching to groups view internally
        }
    }
}
</script>
<style>
/* Force border-box inside this popup */
.add-win,
.add-win *,
.add-win *::before,
.add-win *::after {
    box-sizing: border-box;
}

/* ── Window override ── */
.tc-x-window.add-win {
    padding: 0 !important;
    padding-left: 0 !important;
    border: 1px solid var(--tc-border-primary);
    border-radius: 12px;
    box-shadow: var(--tc-shadow-lg);
    width: 1100px !important;
    min-width: 1100px !important;
    max-width: 1100px !important;
    background: var(--tc-bg-primary);
    color: var(--tc-text-primary);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Hide the default window header */
.add-win > .tc-x-window-head {
    display: none !important;
}

.add-win > .tc-x-window-body {
    padding: 0;
    font-size: 13px;
    width: 1100px;
    overflow: hidden;
}

/* ── Custom Header ── */
.aw-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px 10px;
    cursor: grab;
}

.aw-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--tc-text-primary);
}

.aw-close {
    background: none;
    border: none;
    color: var(--tc-text-tertiary);
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
}

.aw-close:hover {
    background: var(--tc-bg-hover);
    color: var(--tc-text-primary);
}

/* ── Body ── */
.aw-body {
    width: 1100px;
    height: 600px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.aw-body > .indicator-selector {
    height: 100%;
}
</style>
